//AuthContext.tsx
import { useContext, createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import ApiClient from './utils/apiClient';
import type { ApiError } from './utils/apiClient';
import { isProtectedRoute } from './utils/protectedRoutes';

/** Antwort von GET /api/me (weitere Felder je nach Rolle). */
export interface AuthUser {
  id?: string;
  email?: string;
  username?: string;
  role?: string | null;
  acting_as?: { admin_id?: string; admin_email?: string } | null;
  /** Nur Lehrkräfte: Stand des AV-Vertrags (server/services/dpa.py). */
  dpa_accepted_at?: string | null;
  dpa_version?: string | null;
  dpa_current_version?: string;
  [key: string]: unknown;
}

export interface AdminUser {
  id?: string;
  email?: string;
  role: 'admin';
}

export interface LoginResult {
  success: boolean;
  message?: string;
}

export interface AuthContextType {
  isLoggedIn: boolean;
  user: AuthUser | null;
  userRole: string | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<{ success: boolean }>;
  loading: boolean;
  error?: string | null;
  isImpersonating: boolean;
  adminUser: AdminUser | null;
  /** Lädt /api/me neu, z. B. nachdem sich Profildaten serverseitig geändert haben. */
  refreshUser: () => Promise<void>;
}

// Erstelle einen Kontext für die Authentifizierung (Default greift nur ohne Provider)
const AuthContext = createContext<AuthContextType>({
    user: null,
    userRole: null,
    login: async () => ({ success: false, message: 'AuthProvider fehlt' }),
    logout: async () => ({ success: false }),
    loading: true,
    isLoggedIn: false,
    isImpersonating: false,
    adminUser: null,
    refreshUser: async () => {}
});

// Füge eine Liste von Routen hinzu, für die kein Auth-Check durchgeführt werden soll
const noAuthCheckRoutes = [
    '/update-password',
    '/reset-password',
    '/forgot-password',
    '/login',
    '/register'
];

export function AuthProvider({ children }: { children: ReactNode }) {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState<AuthUser | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const lastCheckTimeRef = useRef(0);
    const [isImpersonating, setIsImpersonating] = useState(false);
    const [adminUser, setAdminUser] = useState<AdminUser | null>(null);

    useEffect(() => {
        const sessionExpired = localStorage.getItem('sessionExpired');
        if (sessionExpired) {
            setError('Deine Sitzung ist abgelaufen. Bitte melde Dich erneut an.');
            localStorage.removeItem('sessionExpired');
        }
    }, []);



    const broadcastLogout = useCallback(() => {
        try {
            const authChannel = new BroadcastChannel('auth_status');
            authChannel.postMessage({ type: 'logout' });
            authChannel.close();
        } catch (e) {
            // Ignore BroadcastChannel errors (unsupported browser / privacy mode)
        }
    }, []);

    const handleLogout = useCallback((options: { broadcast?: boolean } = {}) => {
        const shouldBroadcast = options.broadcast === true;
        if (shouldBroadcast) {
            broadcastLogout();
        }
        setIsLoggedIn(false);
        setUser(null);
        setUserRole(null);
        setIsImpersonating(false);
        setAdminUser(null);

        // Prüfe ob aktuelle Route geschützt ist
        const currentPath = window.location.pathname;
        if (isProtectedRoute(currentPath)) {
            // Redirect zur Home-Seite
            window.location.href = '/';
        }
    }, [broadcastLogout]);
    // Überprüfe die Authentifizierung beim Laden
    const checkAuth = useCallback(async (force = false) => {
        // Verhindere zu häufige Auth-Checks (außer bei force)
        const now = Date.now();
        if (!force && now - lastCheckTimeRef.current < 5000) { // Minimum 5 Sekunden zwischen Checks
            return;
        }
        lastCheckTimeRef.current = now;

        try {
            const response = await ApiClient.get('me');
            const data: AuthUser = await response.json();

            console.log('AuthContext - Received data from /api/me:', data);
            setIsLoggedIn(true);
            setUser(data);
            setUserRole(data.role ?? null);

            // Prüfe auf Impersonation
            if (data.acting_as) {
                console.log('AuthContext - Impersonation detected:', data.acting_as);
                setIsImpersonating(true);
                setAdminUser({
                    id: data.acting_as.admin_id,
                    email: data.acting_as.admin_email,
                    role: 'admin'
                });
            } else {
                console.log('AuthContext - No impersonation (acting_as is missing)');
                // Zusätzliche Prüfung: Wenn Admin, prüfe direkt den Admin-Status
                if (data.role === 'admin') {
                    try {
                        const adminStatusResponse = await ApiClient.get('admin/status');
                        const adminStatus = await adminStatusResponse.json();
                        console.log('AuthContext - Admin status check:', adminStatus);
                        if (adminStatus.isImpersonating && adminStatus.targetUser) {
                            console.log('AuthContext - Impersonation found via admin/status, updating state');
                            setIsImpersonating(true);
                            setUserRole(adminStatus.targetUser.role);
                            setUser({
                                ...data,
                                role: adminStatus.targetUser.role,
                                email: adminStatus.targetUser.email,
                                username: adminStatus.targetUser.name,
                                id: adminStatus.targetUser.id,
                                acting_as: {
                                    admin_id: data.id,
                                    admin_email: data.email
                                }
                            });
                            setAdminUser({
                                id: data.id,
                                email: data.email,
                                role: 'admin'
                            });
                            return; // Früh zurückkehren, da wir die Daten bereits gesetzt haben
                        }
                    } catch (err) {
                        console.warn('AuthContext - Could not check admin status:', err);
                    }
                }
                setIsImpersonating(false);
                setAdminUser(null);
            }
        } catch (caught) {
            const error = caught as ApiError;
            // 401 auf /me bedeutet nicht eingeloggt - handleLogout aufrufen
            if (error.status === 401) {
                handleLogout({ broadcast: true });
            } else {
                console.error('Error verifying auth:', error);
            }
        } finally {
            setLoading(false);
        }
    }, [handleLogout]);


    useEffect(() => {
        const currentPath = window.location.pathname;
        const shouldSkipAuthCheck = noAuthCheckRoutes.some(route => currentPath.startsWith(route));

        if (!shouldSkipAuthCheck) {
            let interval: ReturnType<typeof setInterval> | undefined;
            let lastActiveTime = Date.now();

            const handleVisibilityChange = () => {
                if (document.hidden) {
                    clearInterval(interval);
                    lastActiveTime = Date.now();
                } else {
                    // Prüfe wie lange der Tab inaktiv war
                    const inactiveTime = Date.now() - lastActiveTime;
                    if (inactiveTime > 300000) { // 5 Minuten
                        // Erzwinge sofortigen Auth-Check nach längerer Inaktivität
                        checkAuth();
                    }
                    interval = setInterval(checkAuth, 300000);
                }
            };

            // Broadcast Channel für Tab-übergreifende Kommunikation
            const authChannel = new BroadcastChannel('auth_status');
            authChannel.onmessage = (event) => {
                if (event.data.type === 'logout') {
                    handleLogout({ broadcast: false });
                }
            };

            if (!document.hidden) {
                checkAuth();
                interval = setInterval(checkAuth, 300000);
            }

            document.addEventListener('visibilitychange', handleVisibilityChange);

            return () => {
                clearInterval(interval);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                authChannel.close();
            };
        } else {
            // I2: Auf noAuthCheckRoutes wird checkAuth nicht aufgerufen. Ohne dies bliebe
            // loading ewig true und ProtectedRoutes (I1-Guard) würde nach einer Navigation
            // dorthin im Spinner hängen. Nur das periodische Polling wird hier unterdrückt.
            setLoading(false);
        }
    }, [checkAuth, handleLogout]);


    // Login-Funktion (I5: useCallback, damit der Provider-Value stabil bleibt)
    const login = useCallback(async (username: string, password: string): Promise<LoginResult> => {
        try {
            await ApiClient.post('auth/login', {
                email: username,
                password
            });
            await checkAuth(true);  // Force bypass throttle after login
            return { success: true, message: "success" };
        } catch (caught) {
            const error = caught as ApiError;
            console.error('Error during login:', error);
            // error.originalErrorData?.detail enthält die Server-Fehlermeldung
            const message = error.originalErrorData?.detail || error.message;
            return { success: false, message };
        }
    }, [checkAuth]);

    // Logout-Funktion (I5: useCallback)
    const logout = useCallback(async (): Promise<{ success: boolean }> => {
        try {
            await ApiClient.post('auth/logout', {});
            handleLogout({ broadcast: true });
            return { success: true };
        } catch (error) {
            console.error('Error during logout:', error);
            // Auch bei Fehler lokal ausloggen (Server-Session könnte bereits abgelaufen sein)
            handleLogout({ broadcast: true });
            return { success: false };
        }
    }, [handleLogout]);

    // Event Listener für 401 Fehler (von ApiClient gefeuert)
    useEffect(() => {
        const handleUnauthorized = () => {
            // handleLogout ist idempotent - sicher mehrfach aufzurufen
            handleLogout({ broadcast: true });
        };

        window.addEventListener('unauthorized', handleUnauthorized);
        return () => window.removeEventListener('unauthorized', handleUnauthorized);
    }, [handleLogout]);

    // I5: Provider-Value memoisieren, damit useAuth()-Consumer (~12 Komponenten inkl.
    // Navbar, Routen) nicht bei jedem AuthProvider-Render neu rendern.
    const refreshUser = useCallback(() => checkAuth(true), [checkAuth]);

    const value = useMemo<AuthContextType>(() => ({
        isLoggedIn,
        user,
        userRole,
        login,
        logout,
        loading,
        error,
        isImpersonating,
        adminUser,
        refreshUser
    }), [isLoggedIn, user, userRole, login, logout, loading, error, isImpersonating, adminUser, refreshUser]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextType {
    return useContext(AuthContext);
}
